import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './style.css';

import http from '../../libs/http';
import alert from '../../libs/alert';
import analytics from '../../libs/analytics';

function Login({ setUser }: any) {
  const [isSignUp, setIsSignUp] = useState(false);
  const [isLogin, setIsLogin] = useState(false);
  const [isRecovery, setIsRecovery] = useState(false);
  const [isPendingVerification, setIsPendingVerification] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [loading, setLoading] = useState(false);
  const codeRefs = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    analytics.loginPageViewed();
  }, []);

  // Resend cooldown countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  function formatWhatsapp(value: string): string {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 2) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`;
    if (cleaned.length <= 10) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7, 11)}`;
  }

  function handleWhatsappChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    if (value.length <= 15) {
      setWhatsapp(formatWhatsapp(value));
    }
  }

  function validateBrazilianPhone(phone: string): boolean {
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length >= 10 && cleaned.length <= 11;
  }

  function getRawWhatsapp(phone: string): string {
    return phone.replace(/\D/g, '');
  }

  function isFormValid(): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const rawWhatsapp = getRawWhatsapp(whatsapp);
    return (
      name.trim().length > 0 &&
      emailRegex.test(email) &&
      password.length > 0 &&
      rawWhatsapp.length >= 10 &&
      rawWhatsapp.length <= 11 &&
      termsAccepted
    );
  }

  function getFullCode(): string {
    return verificationCode.join('');
  }

  function handleCodeChange(index: number, value: string) {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newCode = [...verificationCode];
    newCode[index] = digit;
    setVerificationCode(newCode);
    if (digit && index < 5) {
      codeRefs.current[index + 1]?.focus();
    }
  }

  function handleCodeKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
  }

  function handleCodePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = ['', '', '', '', '', ''];
    pasted.split('').forEach((char, i) => { newCode[i] = char; });
    setVerificationCode(newCode);
    const nextEmpty = Math.min(pasted.length, 5);
    codeRefs.current[nextEmpty]?.focus();
  }

  async function handleSignUpFormSubmit(event: any) {
    event.preventDefault();

    if (!termsAccepted) {
      alert.error('Você precisa aceitar os termos de uso para se cadastrar!');
      return;
    }

    if (!whatsapp) {
      alert.error('O número de WhatsApp é obrigatório para o cadastro!');
      return;
    }

    if (!validateBrazilianPhone(whatsapp)) {
      alert.error('Por favor, insira um número de WhatsApp válido brasileiro (com DDD)!');
      return;
    }

    analytics.registerSubmitted();
    setLoading(true);

    const url = `${process.env.REACT_APP_API_URL}/api/v1/user/create`;
    const payload = { name, email, password, termsAccepted, whatsapp: getRawWhatsapp(whatsapp) };
    const response = await http.post(url, payload);

    if (response.error) {
      alert.error(response.error);
      setLoading(false);
      return;
    }

    if (response.pendingVerification) {
      setPendingEmail(response.email);
      setIsPendingVerification(true);
      setResendCooldown(60);
      setLoading(false);
      return;
    }

    analytics.registerCompleted();
    setUser(response);
    setLoading(false);
    localStorage.setItem('user', JSON.stringify(response));
    navigate('/');
  }

  async function handleVerifyFormSubmit(event: any) {
    event.preventDefault();
    const code = getFullCode();
    if (code.length < 6) {
      alert.error('Digite o código completo de 6 dígitos.');
      return;
    }

    setLoading(true);

    const url = `${process.env.REACT_APP_API_URL}/api/v1/user/verify-email`;
    const response = await http.post(url, { email: pendingEmail, code });

    if (response.error) {
      alert.error(response.error);
      setLoading(false);
      return;
    }

    analytics.registerCompleted();
    setUser(response);
    setLoading(false);
    localStorage.setItem('user', JSON.stringify(response));
    navigate('/');
  }

  async function handleResendCode() {
    if (resendCooldown > 0) return;

    const url = `${process.env.REACT_APP_API_URL}/api/v1/user/resend-verification`;
    const { error, message } = await http.post(url, { email: pendingEmail });

    if (error) {
      alert.error(error);
      return;
    }

    alert.success(message);
    setResendCooldown(60);
    setVerificationCode(['', '', '', '', '', '']);
    codeRefs.current[0]?.focus();
  }

  async function isFounder(user: any): Promise<boolean> {
    const url = `${process.env.REACT_APP_API_URL}/api/v1/user/medals`;
    const headers = { Authorization: user.token };
    const response = await http.get(url, headers);

    if (!response || !response.length) {
      return false;
    };

    if (response.find((el: any) => el.medalId == 1)) {
      return true;
    }

    return false;
  }

  async function handleLoginFormSubmit(event: any) {
    event.preventDefault();
    analytics.loginSubmitted();
    setLoading(true);

    const url = `${process.env.REACT_APP_API_URL}/api/v1/user/authenticate`;
    const payload = { email, password };
    const response = await http.post(url, payload);

    if (response.error) {
      alert.warn(response.error);
      setLoading(false);
      return;
    }

    response.isFounder = await isFounder(response);

    analytics.loginCompleted();
    setUser(response);
    setLoading(false);
    localStorage.setItem('user', JSON.stringify(response));
    navigate('/');
  }

  async function handleRecoveryFormSubmit(event: any) {
    event.preventDefault();
    setLoading(true);

    const url = `${process.env.REACT_APP_API_URL}/api/v1/user/recovery`;
    const payload = { email };
    const { error, message } = await http.post(url, payload);

    if (error) {
      alert.error(error);
      setLoading(false);
      return;
    }

    alert.success(message);
    setLoading(false);
    resetStates();
  }

  function resetStates() {
    setIsSignUp(false);
    setIsLogin(false);
    setIsRecovery(false);
    setIsPendingVerification(false);
    setPendingEmail('');
    setName('');
    setEmail('');
    setPassword('');
    setTermsAccepted(false);
    setVerificationCode(['', '', '', '', '', '']);
    setResendCooldown(0);
  }

  return (
    <div className="Login-page">
      <div className="Login-content">

      {/* ── Email verification step ── */}
      { isPendingVerification &&
        <div>
          <div className="Login-verify-header">
            <div className="Login-verify-icon">✉️</div>
            <h2 className="Login-verify-title">Verifique seu e-mail</h2>
            <p className="Login-verify-subtitle">
              Enviamos um código de 6 dígitos para<br />
              <strong>{pendingEmail}</strong>
            </p>
          </div>

          <form onSubmit={handleVerifyFormSubmit}>
            <div className="Login-code-inputs">
              {verificationCode.map((digit, index) => (
                <input
                  key={index}
                  ref={el => { codeRefs.current[index] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleCodeChange(index, e.target.value)}
                  onKeyDown={e => handleCodeKeyDown(index, e)}
                  onPaste={index === 0 ? handleCodePaste : undefined}
                  className={`Login-code-box${digit ? ' Login-code-box--filled' : ''}`}
                  autoFocus={index === 0}
                  autoComplete="off"
                />
              ))}
            </div>

            <button
              className="App-button mt-20"
              disabled={getFullCode().length < 6 || loading}
              style={{ opacity: getFullCode().length === 6 ? 1 : 0.5, cursor: getFullCode().length === 6 ? 'pointer' : 'not-allowed' }}
            >
              {loading ? 'Verificando...' : 'Confirmar código'}
            </button>
          </form>

          <div className="Login-verify-resend">
            {resendCooldown > 0 ? (
              <span className="Login-verify-resend-wait">
                Reenviar código em {resendCooldown}s
              </span>
            ) : (
              <button className="Login-verify-resend-btn" onClick={handleResendCode}>
                Não recebeu o código? <span>Reenviar</span>
              </button>
            )}
          </div>

          <button
            className="App-button-no-border mt-10"
            onClick={resetStates}
          >
            Voltar
          </button>
        </div>
      }

      {/* ── Sign up ── */}
      { isSignUp && !isRecovery && !isPendingVerification &&
        <div>
          <form onSubmit={handleSignUpFormSubmit}>
            <div>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onFocus={() => analytics.registerFieldFocused('name')}
                className="App-input mb-10"
                placeholder='Nome'
                required
              />
            </div>
            <div>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => analytics.registerFieldFocused('email')}
                className="App-input mb-10"
                placeholder='E-mail'
                type='email'
                required
              />
            </div>
            <div>
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => analytics.registerFieldFocused('password')}
                className="App-input"
                placeholder='Senha'
                type='password'
              />
            </div>
            <div>
              <input
                value={whatsapp}
                onChange={handleWhatsappChange}
                onFocus={() => analytics.registerFieldFocused('whatsapp')}
                className="App-input mt-10"
                placeholder='WhatsApp (ex: 11 99999-9999)'
                type='tel'
                required
              />
            </div>
            <div className="mt-15">
              <label className="terms-label">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => { setTermsAccepted(e.target.checked); if (e.target.checked) analytics.registerTermsAccepted(); }}
                  className="terms-input"
                />
                <span className="terms-text">
                  Li e aceito os{' '}
                  <Link to="/termos" target="_blank" className="terms-link">
                    Termos de Uso
                  </Link>
                </span>
              </label>
            </div>
            <button
              className="App-button mt-20"
              disabled={!isFormValid() || loading}
              style={{ opacity: isFormValid() ? 1 : 0.5, cursor: isFormValid() ? 'pointer' : 'not-allowed' }}
            >
              { loading ? 'Inscrevendo-se...' : 'Inscreva-se' }
            </button>
          </form>
          <button
            className="App-button-no-border mt-10"
            onClick={resetStates}
          >
            Voltar
          </button>
        </div>
      }

      {/* ── Login ── */}
      { isLogin && !isRecovery &&
        <div>
          <form onSubmit={handleLoginFormSubmit}>
            <div>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="App-input mb-10"
                placeholder='E-mail'
                required
              />
            </div>
            <div>
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="App-input"
                placeholder='Senha'
                type='password'
                required
              />
            </div>
            <button
              className="App-button mt-20"
            >
              { loading ? 'Entrando...' : 'Entrar' }
            </button>
          </form>
          <button
            className="App-button-no-border mt-10"
            onClick={resetStates}
          >
            Voltar
          </button>
        </div>
      }

      {/* ── Landing buttons ── */}
      { !isSignUp && !isLogin && !isRecovery && !isPendingVerification &&
        <>
          <button
            className="Login-button-main"
            onClick={() => { analytics.loginFormOpened(); setIsLogin(true); }}
          >
            Entre e bora conversar
          </button>
          <button
            type="button"
            className="Login-signup-text mt-10"
            onClick={() => { analytics.registerFormOpened(); setIsSignUp(true); }}
          >
            Inscreva-se e junte-se a nós!
          </button>
        </>
      }

      {/* ── Recovery ── */}
      { !isSignUp && !isLogin && isRecovery &&
        <div>
          <form onSubmit={handleRecoveryFormSubmit}>
            <div>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="App-input"
                placeholder='E-mail'
                required
              />
            </div>
            <button
              className="App-button mt-20"
            >
              { loading ? 'Recuperando...' : 'Recuperar' }
            </button>
          </form>
          <button
            className="App-button-no-border mt-10"
            onClick={resetStates}
          >
            Voltar
          </button>
        </div>
      }
      </div>
      <div
        className="Login-contact"
        onClick={() => {
          setIsSignUp(false); setIsLogin(false); setIsRecovery(true);
        }}
      >
        Já conversou comigo e perdeu sua conta? <span className="Login-contact-link">Clique aqui e recupere!</span>
      </div>
    </div>
  );
}

export default Login;
